"use client";

import React from "react";
import TrackedLink from "@components/TrackedLink";
import ScheduleCall from "@components/ScheduleCall";
import { track } from "@lib/track";
import { feeLabel } from "@lib/fees";

const PRICE = feeLabel("bodhiClub");

const MONTHLY_INCLUSIONS = [
  { label: "Direct access to a doctor and nutritionist", detail: "Scheduled Q&A windows with both Niki and Dr. Zoue — ask the questions you can't get answered elsewhere" },
  { label: "Monthly expert Zoom with live Q&A", detail: "A focused session on the most-requested topics, with space to ask your own questions in real time" },
  { label: "One emergency express advice call per month", detail: "20 minutes when something comes up and you need a straight answer fast" },
  { label: "Supportive member community", detail: "A group of like-minded people working on their health — not a passive content feed" },
  { label: "Research breakdowns in plain language", detail: "Current medical and nutrition research, explained clearly without the jargon" },
  { label: "Holistic protocols and content library", detail: "Covering fertility, gut-brain health, oncology support, neuro health, weight, glycaemic control and more" },
  { label: "Practical recipes and meal-prep guidance", detail: "Grounded in what members actually need — not generic clean-eating content" },
  { label: "Mental health and breathwork sessions", detail: "Because health is not just physical — and the research backs this up" },
];

const TESTIMONIALS = [
  {
    quote: "Niki's passion and enthusiasm is infectious. Her patience in answering any questions is endless.",
    body: "Everything we learned was accessible and simple to continue at home.",
    name: "Jayne G.",
    tag: "Retreat Guest",
  },
  {
    quote: "Every bit of advice from Niki has made me feel healthier.",
    body: "The gut cleanse was unreal — I felt so much better afterwards, and losing a few kilos was a great side note.",
    name: "Helen",
    tag: "Ongoing Guidance",
  },
  {
    quote: "I have learned so much from Niki's expert guidance.",
    body: "Learning how to make the foods I need to support my gut and liver has been invaluable — something I can incorporate into my regular nutrition forever.",
    name: "Claire",
    tag: "Nutrition Programme",
  },
];

export default function BodhiClubClient() {
  const onPlay = (label: string) => {
    try {
      track(
        "video_play",
        { label, component: "BodhiClubPage.Video" },
        { event: "video_play", label, component: "BodhiClubPage.Video" }
      );
    } catch {}
  };

  const scrollToScheduler: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    document.getElementById("schedule-call")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative bg-[var(--color-brand)] text-[var(--text-on-dark)]">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <header className="mx-auto max-w-5xl px-5 md:px-10 py-12 md:py-20 text-center">
        <p className="text-xs md:text-sm uppercase tracking-widest opacity-70 mb-4 font-semibold">
          The Bodhi Health Club
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-balance">
          Expert Health Guidance, Every Month.
          <br />
          Without the Cost of 1-to-1 Care.
        </h1>
        <p className="mt-5 text-sm md:text-lg opacity-90 max-w-3xl mx-auto leading-relaxed text-pretty">
          Monthly wellness guidance from both a{" "}
          <strong>Naturopathic Nutritionist</strong> and an{" "}
          <strong>Integrative Medicine Doctor</strong> — in a community that keeps you informed,
          supported and moving forward.
        </p>
        <div className="mt-8">
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="lg"
            color="base"
            event="cta_click"
            label="Bodhi Club | Join Hero"
            component="BodhiClubPage"
          >
            Join the Bodhi Club
          </TrackedLink>
        </div>
        <p className="mt-3 text-xs md:text-sm opacity-70">From {PRICE}/month · Cancel any time</p>
      </header>

      {/* ── BODY ─────────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-5xl -mt-6 rounded-t-3xl bg-[var(--bg-surface)] px-5 md:px-10 pb-20 shadow-2xl text-[var(--text-on-light)]">

        {/* ── THE DIFFERENCE ───────────────────────────────────────────────── */}
        <section className="pt-10 md:pt-16 max-w-3xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)]">
            A Doctor and a Nutritionist. In the Same Room.
          </h2>
          <p className="mt-3 text-sm md:text-base text-[var(--text-on-light-muted)] leading-relaxed">
            Most health communities give you one perspective. Bodhi gives you two — and the
            conversation between them is where the real clarity comes from.
          </p>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm text-left">
              <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-semibold mb-2">Naturopathic Nutritionist</p>
              <p className="font-bold text-[var(--text-brand)] text-base">Niki Charnock</p>
              <p className="text-xs text-[var(--text-on-light-muted)] mb-3">
                College of Naturopathic Medicine, London · Member, ANP ·
                BSc Sports Rehabilitation, Salford University
              </p>
              <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">
                Founder of CF Organic Wellness. Niki's approach blends functional nutrition,
                lifestyle medicine and genuine clinical experience — evidence-based,
                holistically delivered.
              </p>
            </div>
            <div className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm text-left">
              <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-semibold mb-2">Integrative Medicine Doctor</p>
              <p className="font-bold text-[var(--text-brand)] text-base">Dr. Zoue Lloyd Wright</p>
              <p className="text-xs text-[var(--text-on-light-muted)] mb-3">MD · Integrative Medicine</p>
              <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">
                Dr. Zoue bridges conventional medicine and integrative care — giving you clarity
                on where medical and holistic approaches complement each other, and where they
                don't.
              </p>
            </div>
          </div>
        </section>

        {/* ── VIDEO ────────────────────────────────────────────────────────── */}
        <section className="mt-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)]">
            A Membership That Moves With You
          </h2>
          <p className="mt-2 text-[var(--text-on-light-muted)] max-w-md mx-auto text-sm md:text-base">
            A quick overview of how Bodhi keeps you supported month to month.
          </p>
          <div className="mt-6">
            <video
              controls
              preload="none"
              poster="/images/Niki_Charnock.webp"
              className="mx-auto w-full md:w-2/4 max-w-2xl rounded-2xl shadow-lg"
              onPlay={() => onPlay("Bodhi Club Overview")}
            >
              <source src="/images/BodhiClub.mp4" type="video/mp4" />
            </video>
          </div>
        </section>

        {/* ── WHAT'S INCLUDED ──────────────────────────────────────────────── */}
        <section className="mt-14 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-2">
            What's Included Every Month
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8">
            Every tier. From day one.
          </p>
          <div className="space-y-3">
            {MONTHLY_INCLUSIONS.map((item, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-4 shadow-sm flex gap-4 items-start">
                <span className="mt-0.5 text-green-600 font-bold shrink-0">✓</span>
                <div>
                  <p className="font-semibold text-sm text-[var(--text-brand)]">{item.label}</p>
                  <p className="text-sm text-[var(--text-on-light-muted)] mt-0.5">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── WHO IT'S FOR ─────────────────────────────────────────────────── */}
        <section className="mt-14 max-w-3xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] mb-6">
            Who the Bodhi Club Is For
          </h2>
          <div className="grid md:grid-cols-3 gap-5 text-left">
            {[
              {
                title: "Health-curious people",
                body: "You want to understand what's actually going on in your body — not just follow advice without knowing why.",
              },
              {
                title: "Parents and families",
                body: "Navigating health decisions for children and families benefits from joined-up guidance — not conflicting opinions from different sources.",
              },
              {
                title: "Farleigh Method graduates",
                body: "You've done the intensive work. Bodhi keeps you on track with lighter ongoing support once you've built your foundation.",
              },
            ].map((card, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm">
                <h3 className="font-semibold text-[var(--text-brand)] mb-2">{card.title}</h3>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
        <section className="mt-14">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-2">
            What Members Say
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8 max-w-2xl mx-auto">
            From clients working with Niki across her programmes.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm flex flex-col gap-3">
                <p className="text-sm font-semibold text-[var(--text-brand)] italic leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed flex-1">
                  {t.body}
                </p>
                <div>
                  <p className="text-sm font-semibold text-[var(--text-brand)]">{t.name}</p>
                  <p className="text-xs text-[var(--text-on-light-muted)]">{t.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── PRICING ──────────────────────────────────────────────────────── */}
        <section className="mt-14 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-8">
            Choose Your Level
          </h2>
          <div className="grid md:grid-cols-2 gap-5">

            {/* Community tier */}
            <div className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-6 shadow-sm flex flex-col gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-semibold mb-1">Community</p>
                <p className="text-3xl font-extrabold text-[var(--text-brand)]">{PRICE}<span className="text-base font-medium">/month</span></p>
                <p className="text-sm text-[var(--text-on-light-muted)] mt-1">Everything in the monthly inclusions above</p>
              </div>
              <ul className="text-sm space-y-1 text-[var(--text-on-light-muted)]">
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Community access</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Monthly Zoom + Q&A</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Expert Q&A windows</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Emergency advice call (20 min)</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Full content library</li>
              </ul>
              <p className="text-xs text-[var(--text-on-light-muted)]">Cancel any time</p>
            </div>

            {/* With 1-to-1 tier */}
            <div className="rounded-2xl border-2 border-[var(--text-brand)] bg-[var(--bg-card)] p-6 shadow-md flex flex-col gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-semibold mb-1">Community + 1-to-1</p>
                <p className="text-3xl font-extrabold text-[var(--text-brand)]">£70<span className="text-base font-medium">/month</span></p>
                <p className="text-sm text-[var(--text-on-light-muted)] mt-1">Everything above, plus a monthly personal check-in</p>
              </div>
              <ul className="text-sm space-y-1 text-[var(--text-on-light-muted)]">
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Everything in Community</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> 30-minute 1-to-1 follow-up each month</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Written notes after each session</li>
                <li className="flex gap-2"><span className="text-green-600 font-bold shrink-0">✓</span> Personalised protocol adjustments</li>
              </ul>
              <p className="text-xs text-[var(--text-on-light-muted)]">Drop to {PRICE}/month any time</p>
            </div>
          </div>
        </section>

        {/* ── SCHEDULER ────────────────────────────────────────────────────── */}
        <section id="schedule-call" className="mt-14 scroll-mt-24">
          <ScheduleCall
            duration={30}
            meetingType="bodhi-club"
            title="Bodhi Club — Welcome Call"
            subtitle="Choose a time to chat with Niki about what you need and which level fits best."
            ctaText="Join the Club"
            componentName="BodhiClubPage"
          />
        </section>

        {/* ── UPGRADE PROMPT ───────────────────────────────────────────────── */}
        <section className="mt-10 max-w-3xl mx-auto rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-6 text-center shadow-sm">
          <h2 className="text-base md:text-lg font-semibold text-[var(--text-brand)]">
            Need more structured support?
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] mt-2 mb-5 max-w-sm mx-auto">
            The Farleigh Method is our signature 1-to-1 programme — structured, adaptive and
            built around your specific situation. Bodhi is where you land after.
          </p>
          <TrackedLink
            href="/workwithme/farleighMethod"
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Bodhi Club | View Farleigh Method"
            component="BodhiClubPage"
          >
            View the Farleigh Method →
          </TrackedLink>
        </section>

        <img
          src="/images/Niki_sig.webp"
          alt="Signature — Niki Charnock"
          className="mt-10 mx-auto"
          style={{ width: 150 }}
        />
      </main>

      {/* ── STICKY MOBILE CTA ────────────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-medium text-[var(--text-on-light)]">From {PRICE}/month</span>
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Bodhi Club | Join Sticky"
            component="BodhiClubPage"
          >
            Join the Club
          </TrackedLink>
        </div>
      </div>
    </div>
  );
}
